import React, { useState, useEffect, useCallback } from 'react';

const API_BASE = 'https://apispeccheck.zonetap.com';
const GOLD = '#D4A417';
const SURFACE = '#13161e';
const PANEL_BG = '#1e2235';
const BORDER = '#252a38';

const getToken = () => localStorage.getItem('auth0_token');
const authHeaders = () => ({ Authorization: `Bearer ${getToken()}`, 'Content-Type': 'application/json' });

export default function PlatformFilePicker({ user, onFileSelect, onClose }) {
  const userId = user?.email || user?.sub || '';

  const [status, setStatus] = useState({ procore: { connected: false, loading: true }, autodesk: { connected: false, loading: true } });
  const [platform, setPlatform] = useState(null);   // 'procore' | 'autodesk'
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(null); // file id being downloaded
  const [error, setError] = useState('');

  // Load integration status on mount
  useEffect(() => {
    fetch(`${API_BASE}/api/integrations/status?userId=${encodeURIComponent(userId)}`, { headers: authHeaders() })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (!data) return;
        const c = data.connections || data;
        setStatus({
          procore: { connected: c.procore?.connected || false, loading: false },
          autodesk: { connected: c.autodesk?.connected || false, loading: false },
        });
      })
      .catch(() => setStatus({ procore: { connected: false, loading: false }, autodesk: { connected: false, loading: false } }));
  }, [userId]);

  const neitherConnected = !status.procore.loading && !status.autodesk.loading
    && !status.procore.connected && !status.autodesk.connected;

  // Load projects for the selected platform
  const openPlatform = useCallback(async (p) => {
    setPlatform(p);
    setSelectedProject(null);
    setFiles([]);
    setError('');
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/files/${p}/projects`, { headers: authHeaders() });
      if (!res.ok) throw new Error('Failed to load projects');
      const data = await res.json();
      setProjects(data.projects || []);
    } catch (e) {
      setError('Could not load projects. Check your connection in Integrations.');
    } finally {
      setLoading(false);
    }
  }, []);

  // Load files for the selected project
  const openProject = useCallback(async (project) => {
    setSelectedProject(project);
    setFiles([]);
    setError('');
    setLoading(true);
    try {
      let fileList = [];
      if (platform === 'procore') {
        const res = await fetch(`${API_BASE}/api/files/procore/projects/${project.id}/documents`, { headers: authHeaders() });
        if (!res.ok) throw new Error('Failed to load files');
        const data = await res.json();
        fileList = data.documents || [];
      } else {
        const res = await fetch(`${API_BASE}/api/files/autodesk/projects/${project.id}/folders`, { headers: authHeaders() });
        if (!res.ok) throw new Error('Failed to load files');
        const data = await res.json();
        fileList = data.folders || [];
      }
      setFiles(fileList);
    } catch (e) {
      setError('Could not load project files.');
    } finally {
      setLoading(false);
    }
  }, [platform]);

  // Download file and call onFileSelect
  const selectFile = useCallback(async (file) => {
    setDownloading(file.id);
    setError('');
    try {
      const res = await fetch(`${API_BASE}/api/files/${platform}/download`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ userId, fileId: file.id, fileName: file.name, downloadUrl: file.downloadUrl || null }),
      });
      if (!res.ok) throw new Error('Download failed');
      const data = await res.json();

      const byteString = atob(data.buffer);
      const ab = new ArrayBuffer(byteString.length);
      const ia = new Uint8Array(ab);
      for (let i = 0; i < byteString.length; i++) ia[i] = byteString.charCodeAt(i);
      const blob = new Blob([ab], { type: 'application/pdf' });
      const fileObj = new File([blob], file.name, { type: 'application/pdf' });

      onFileSelect(fileObj);
      onClose();
    } catch (e) {
      setError(`Could not download "${file.name}". Try again.`);
    } finally {
      setDownloading(null);
    }
  }, [platform, userId, onFileSelect, onClose]);

  // Close on overlay click
  const handleOverlayClick = (e) => { if (e.target === e.currentTarget) onClose(); };

  return (
    <div
      onClick={handleOverlayClick}
      style={{
        position: 'fixed', inset: 0, zIndex: 2000,
        background: 'rgba(0,0,0,0.65)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontFamily: 'sans-serif',
      }}
    >
      <div style={{
        width: 600, maxHeight: '80vh',
        background: SURFACE,
        border: `1px solid ${BORDER}`,
        borderRadius: 12,
        display: 'flex', flexDirection: 'column',
        boxShadow: '0 24px 48px rgba(0,0,0,0.5)',
        overflow: 'hidden',
      }}>

        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '16px 20px',
          borderBottom: `1px solid ${BORDER}`,
          flexShrink: 0,
        }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: '#f3f4f6' }}>📁 Platform Files</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#7a8099', fontSize: 20, cursor: 'pointer', lineHeight: 1, padding: 0 }}>✕</button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px' }}>

          {/* Neither connected */}
          {neitherConnected && (
            <div style={{ textAlign: 'center', padding: '32px 16px', color: '#7a8099', fontSize: 14 }}>
              <div style={{ fontSize: 32, marginBottom: 12 }}>🔌</div>
              <div style={{ fontWeight: 600, color: '#cdd1dc', marginBottom: 8 }}>No platforms connected</div>
              <div>Connect Procore or Autodesk in the <span style={{ color: GOLD }}>Integrations</span> tab to pick files from your platform.</div>
            </div>
          )}

          {/* Loading status */}
          {(status.procore.loading || status.autodesk.loading) && (
            <div style={{ color: '#7a8099', fontSize: 13, textAlign: 'center', padding: 24 }}>Checking connections…</div>
          )}

          {/* Platform selection */}
          {!neitherConnected && !status.procore.loading && !status.autodesk.loading && !platform && (
            <div>
              <div style={{ fontSize: 12, color: '#7a8099', marginBottom: 14, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Select platform</div>
              <div style={{ display: 'flex', gap: 12 }}>
                {status.procore.connected && (
                  <button onClick={() => openPlatform('procore')} style={platformBtnStyle}>
                    <span style={{ fontSize: 24 }}>🏗️</span>
                    <span style={{ fontWeight: 600, color: '#f3f4f6' }}>Procore</span>
                  </button>
                )}
                {status.autodesk.connected && (
                  <button onClick={() => openPlatform('autodesk')} style={platformBtnStyle}>
                    <span style={{ fontSize: 24 }}>🔷</span>
                    <span style={{ fontWeight: 600, color: '#f3f4f6' }}>Autodesk</span>
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Breadcrumb nav */}
          {platform && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 16, fontSize: 12, color: '#7a8099' }}>
              <button onClick={() => { setPlatform(null); setProjects([]); setSelectedProject(null); setFiles([]); }} style={crumbBtn}>
                Platforms
              </button>
              <span>›</span>
              <button onClick={() => { setSelectedProject(null); setFiles([]); }} style={selectedProject ? crumbBtn : crumbActive}>
                {platform === 'procore' ? '🏗️' : '🔷'} {platform.charAt(0).toUpperCase() + platform.slice(1)}
              </button>
              {selectedProject && (
                <>
                  <span>›</span>
                  <span style={crumbActive}>{selectedProject.name}</span>
                </>
              )}
            </div>
          )}

          {/* Loading spinner */}
          {loading && (
            <div style={{ color: '#7a8099', fontSize: 13, textAlign: 'center', padding: '24px 0' }}>Loading…</div>
          )}

          {/* Error */}
          {error && (
            <div style={{ color: '#f87171', fontSize: 13, padding: '10px 14px', background: 'rgba(239,68,68,0.1)', borderRadius: 6, marginBottom: 12 }}>
              {error}
            </div>
          )}

          {/* Project list */}
          {platform && !selectedProject && !loading && projects.length > 0 && (
            <div>
              <div style={{ fontSize: 12, color: '#7a8099', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Projects</div>
              {projects.map(p => (
                <button key={p.id} onClick={() => openProject(p)} style={rowStyle}>
                  <span style={{ fontSize: 16 }}>📂</span>
                  <span style={{ flex: 1, textAlign: 'left', color: '#e5e7eb', fontSize: 13 }}>{p.name}</span>
                  <span style={{ color: '#7a8099', fontSize: 12 }}>›</span>
                </button>
              ))}
            </div>
          )}

          {platform && !selectedProject && !loading && projects.length === 0 && !error && (
            <div style={{ color: '#7a8099', fontSize: 13, textAlign: 'center', padding: 24 }}>No projects found.</div>
          )}

          {/* File list */}
          {selectedProject && !loading && files.length > 0 && (
            <div>
              <div style={{ fontSize: 12, color: '#7a8099', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Files</div>
              {files.map(f => (
                <button
                  key={f.id}
                  onClick={() => selectFile(f)}
                  disabled={downloading === f.id}
                  style={{ ...rowStyle, opacity: downloading === f.id ? 0.6 : 1 }}
                >
                  <span style={{ fontSize: 16 }}>📄</span>
                  <span style={{ flex: 1, textAlign: 'left', fontSize: 13 }}>
                    <span style={{ color: '#e5e7eb', display: 'block' }}>{f.name}</span>
                    {f.type && <span style={{ color: '#7a8099', fontSize: 11 }}>{f.type}</span>}
                  </span>
                  {downloading === f.id
                    ? <span style={{ color: GOLD, fontSize: 12 }}>Downloading…</span>
                    : <span style={{ color: GOLD, fontSize: 12 }}>Select ›</span>
                  }
                </button>
              ))}
            </div>
          )}

          {selectedProject && !loading && files.length === 0 && !error && (
            <div style={{ color: '#7a8099', fontSize: 13, textAlign: 'center', padding: 24 }}>No files found in this project.</div>
          )}
        </div>
      </div>
    </div>
  );
}

const platformBtnStyle = {
  flex: 1,
  display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8,
  padding: '20px 16px',
  background: PANEL_BG,
  border: `1px solid ${BORDER}`,
  borderRadius: 10,
  cursor: 'pointer',
  fontFamily: 'sans-serif',
  transition: 'border-color 0.15s',
};

const rowStyle = {
  width: '100%',
  display: 'flex', alignItems: 'center', gap: 10,
  padding: '10px 12px',
  marginBottom: 4,
  background: PANEL_BG,
  border: `1px solid ${BORDER}`,
  borderRadius: 7,
  cursor: 'pointer',
  fontFamily: 'sans-serif',
};

const crumbBtn = {
  background: 'none', border: 'none', cursor: 'pointer',
  color: GOLD, fontSize: 12, padding: 0, fontFamily: 'sans-serif',
};

const crumbActive = {
  color: '#cdd1dc', fontSize: 12,
};
